import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { extraApi, projectApi, priceListApi, estimateCategoryApi } from '../lib/api';
import { getErrorMessage } from '../lib/errorUtils';
import { ReclassifyDialog } from '../components/extras/ReclassifyDialog';
import { ExtraStornoDialog } from '../components/extras/ExtraStornoDialog';
import { CorrectionDetailDialog, CORRECTION_STATUS_LABELS, CORRECTION_STATUS_TONE } from '../components/extras/CorrectionDetailDialog';
import { correctionApi } from '../lib/api';
import { FileMinus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Download } from 'lucide-react';

// Import extracted components
import {
  ExtraStatusCard,
  ExtraInfoCard,
  ExtraCategoryTabs,
  ExtraItemsTable,
  ExtraAddItemModal,
  ExtraSummaryCard,
  ChangeCategoryDialog,
  RejectDialog,
  UploadSignedDialog,
  UploadSupplierDialog,
  ModificationDialog,
  DiscountDialog
} from '../components/extras';

// Configurazione categorie (stesse del preventivo)
const CATEGORIES = [
  { code: 'A', label: 'PROGETTAZIONE', color: '#FF7819' },
  { code: 'B', label: 'PRATICHE', color: '#FF7819' },
  { code: 'C', label: 'OPERE EDILI', color: '#FF7819', family: 'lavoro' },
  { code: 'D', label: 'IMPIANTI ELETTRICI', color: '#FF7819', family: 'lavoro' },
  { code: 'E', label: 'IMPIANTI IDRAULICI, TERMICI E CLIMATIZZAZIONE', color: '#FF7819', family: 'lavoro' },
  { code: 'F', label: 'FINITURE', color: '#FF7819', family: 'fornitura' },
  { code: 'G', label: 'SERRAMENTI', color: '#FF7819', family: 'fornitura' },
  { code: 'H', label: 'PRODOTTI', color: '#FF7819', family: 'fornitura' },
  { code: 'S', label: 'SICUREZZA', color: '#FF7819' },
];

// Categorie lavoro vs fornitura
const LAVORO_CATEGORIES = ['C', 'D', 'E'];
const FORNITURA_CATEGORIES = ['F', 'G', 'H'];

const ExtraDetailPage = () => {
  const { id, projectId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAdmin, isTechnician } = useAuth();
  const { registerGuard, unregisterGuard, setBlocking, attemptNavigation } = useNavigationGuard();
  
  // isNew è true se siamo nella route /extras/new (id non presente) o id === 'new'
  const isNew = !id || id === 'new';
  const projectIdFromUrl = projectId || searchParams.get('project_id');
  
  // State
  const [extra, setExtra] = useState(null);
  const [project, setProject] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showUploadSignedDialog, setShowUploadSignedDialog] = useState(false);
  const [selectedSignedFile, setSelectedSignedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showUploadSupplierDialog, setShowUploadSupplierDialog] = useState(false);
  const [selectedSupplierFile, setSelectedSupplierFile] = useState(null);
  const [supplierDocType, setSupplierDocType] = useState('preventivo');
  
  // Nuovo flusso - Richiesta modifica e sconto
  const [showModificationDialog, setShowModificationDialog] = useState(false);
  const [modificationNotes, setModificationNotes] = useState('');
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  
  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const initialFormDataRef = useRef(null);
  
  // UI State
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showReclassifyDialog, setShowReclassifyDialog] = useState(false);
  // STEP 5B-1 · pricing policy LAVORI del progetto (coefficienti per categoria)
  const [pricingPolicy, setPricingPolicy] = useState(null);
  const [showChangeCategoryDialog, setShowChangeCategoryDialog] = useState(false);
  const [pendingCategory, setPendingCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  
  // Filtro categorie in base al ruolo
  const isAdminOrAdministration = isAdmin || user?.role === 'administration' || user?.role === 'project_manager';

  // Extra Storno / Correction (Phase 2B)
  const [stornoOpen, setStornoOpen] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [correctionDetailId, setCorrectionDetailId] = useState(null);
  const loadCorrections = React.useCallback(async () => {
    if (!id || isNew) return;
    try { const r = await correctionApi.list(id); setCorrections(r.data || []); } catch (_) { /* silente */ }
  }, [id, isNew]);
  useEffect(() => { loadCorrections(); }, [loadCorrections]);
  const reloadAfterCorrection = async () => {
    try { const r = await extraApi.getOne(id); setExtra(r.data); } catch (_) {}
    loadCorrections();
  };
  const csState = extra?.corrections_summary?.economic_correction_state;
  const extraEffective = !!(extra && (extra.signed_at || extra.signed_pdf_url ||
    ['approvato', 'validato', 'firmato_cliente', 'accettato_cliente', 'ordinato', 'completato'].includes(extra.approval_status)));
  const canStorno = !isNew && isAdmin && extraEffective && csState !== 'stornato';
  const FILTERED_CATEGORIES = isAdminOrAdministration 
    ? CATEGORIES 
    : CATEGORIES.filter(cat => ['C', 'D', 'E', 'S'].includes(cat.code));
  
  // Note editing state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    project_id: projectIdFromUrl || '',
    title: '',
    extra_type: 'lavoro',
    items: [],
    category_notes: {},
    notes: ''
  });

  // Refs for navigation guard
  const formDataRef = useRef(formData);

  // Keep refs updated
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // New item form
  const [newItem, setNewItem] = useState({
    price_item_id: '',
    code: '',
    description: '',
    category: '',
    subcategory: '',
    quantity: 1,
    unit: '',
    unit_price: 0,
    vat_percentage: 10,
    notes: ''
  });

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Sync with navigation guard context
  useEffect(() => {
    setBlocking(hasUnsavedChanges);
  }, [hasUnsavedChanges, setBlocking]);

  // Save function for navigation guard
  const handleSaveForGuard = useCallback(async () => {
    const currentFormData = formDataRef.current;
    
    if (!currentFormData.project_id) {
      toast.error('Progetto non valido');
      return;
    }
    
    setSaving(true);
    try {
      const total = currentFormData.items.reduce((sum, item) => sum + (item.total || 0), 0);
      
      const payload = {
        ...currentFormData,
        total
      };
      
      if (isNew) {
        await extraApi.create(payload);
        toast.success('Extra creato');
      } else {
        await extraApi.update(id, payload);
        toast.success('Extra salvato');
      }
      setHasUnsavedChanges(false);
      initialFormDataRef.current = JSON.stringify(currentFormData);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  }, [isNew, id]);

  // Register navigation guard
  useEffect(() => {
    registerGuard({
      onSave: handleSaveForGuard,
      onConfirm: (path) => navigate(path)
    });
    return () => unregisterGuard();
  }, [registerGuard, unregisterGuard, navigate, handleSaveForGuard]);

  // Safe navigation
  const safeNavigate = useCallback((path) => {
    if (hasUnsavedChanges) {
      attemptNavigation(path);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate, attemptNavigation]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        const catRes = await estimateCategoryApi.getAll();
        setCategories(catRes.data);
        
        const priceRes = await priceListApi.getAll();
        setPriceList(priceRes.data);
        
        if (projectIdFromUrl) {
          const projRes = await projectApi.getOne(projectIdFromUrl);
          setProject(projRes.data);
          
          if (isTechnician && projRes.data.status === 'chiuso') {
            toast.error('Non puoi creare extra su progetti chiusi');
            navigate(`/projects/${projectIdFromUrl}`);
            return;
          }
        }
        
        if (!isNew && id) {
          const extraRes = await extraApi.getOne(id);
          setExtra(extraRes.data);
          
          if (extraRes.data.project_id) {
            const projRes = await projectApi.getOne(extraRes.data.project_id);
            setProject(projRes.data);
          }
          
          setFormData({
            project_id: extraRes.data.project_id,
            title: extraRes.data.title || '',
            extra_type: extraRes.data.extra_type || 'lavoro',
            items: extraRes.data.items || [],
            category_notes: extraRes.data.category_notes || {},
            notes: extraRes.data.notes || ''
          });
          
          if (extraRes.data.items?.length > 0) {
            const firstCategory = extraRes.data.items[0]?.category || 'C';
            setActiveCategory(firstCategory);
          }
          
          initialFormDataRef.current = JSON.stringify(formData);
          setTimeout(() => setInitialLoadComplete(true), 100);
        } else {
          // STEP 4F.2 · Query param ?type= determina SOLO lo stato iniziale del tipo Extra.
          // Dopo il caricamento, la dropdown resta autoritativa (nessun override continuo).
          const typeParam = searchParams.get('type');
          const initType = (typeParam === 'lavoro' || typeParam === 'fornitura') ? typeParam : 'lavoro';
          setFormData(prev => ({
            ...prev,
            project_id: projectIdFromUrl || '',
            extra_type: initType
          }));
          setTimeout(() => {
            initialFormDataRef.current = JSON.stringify({
              project_id: projectIdFromUrl || '',
              title: '',
              extra_type: initType,
              items: [],
              category_notes: {},
              notes: ''
            });
            setInitialLoadComplete(true);
          }, 100);
        }
        
        if (!activeCategory) {
          setActiveCategory('C');
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isNew, projectIdFromUrl, isTechnician, navigate]);

  // Track changes
  useEffect(() => {
    if (!initialLoadComplete) return;
    
    if (initialFormDataRef.current) {
      const currentData = JSON.stringify(formData);
      const hasChanges = currentData !== initialFormDataRef.current;
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, initialLoadComplete]);

  // Get subcategories for active category
  const subcategories = useMemo(() => {
    const cat = categories.find(c => c.code === activeCategory);
    return cat?.subcategories || [];
  }, [categories, activeCategory]);

  // STEP 5B-1 · carica la policy pricing LAVORI (per proporre il netto = listino * coeff).
  useEffect(() => {
    const pid = formData.project_id;
    if (!pid || user?.role === 'external_technician') { setPricingPolicy(null); return; }
    let cancelled = false;
    extraApi.getLavoriPricingPolicy(pid)
      .then(res => { if (!cancelled) setPricingPolicy(res.data); })
      .catch(() => { if (!cancelled) setPricingPolicy(null); });
    return () => { cancelled = true; };
  }, [formData.project_id, user?.role]);

  // STEP 5B-1 · netto proposto = listino * coefficiente categoria (arrotondato 2 decimali).
  const resolveProposedNet = useCallback((category, listPrice) => {
    if (listPrice == null) return 0;
    const cats = pricingPolicy?.categories || {};
    const coeff = cats[category]?.coefficient ?? pricingPolicy?.global_coefficient ?? 1;
    return Math.round(Number(listPrice) * coeff * 100) / 100;
  }, [pricingPolicy]);

  // Filter price list by active category and search
  const filteredPriceList = useMemo(() => {
    return priceList.filter(item => {
      const matchesCategory = item.category === activeCategory;
      const matchesSubcategory = selectedSubcategory === 'all' || item.subcategory === selectedSubcategory;
      const matchesSearch = !searchTerm || 
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSubcategory && matchesSearch;
    });
  }, [priceList, activeCategory, selectedSubcategory, searchTerm]);

  // Get items for active category
  const categoryItems = useMemo(() => {
    return formData.items.filter(item => item.category === activeCategory);
  }, [formData.items, activeCategory]);

  // Calculate category total
  const categoryTotal = useMemo(() => {
    return categoryItems.reduce((sum, item) => sum + (item.total || 0), 0);
  }, [categoryItems]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    return formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
  }, [formData.items]);

  // Count items per category
  const categoryItemCounts = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach(cat => {
      counts[cat.code] = formData.items.filter(item => item.category === cat.code).length;
    });
    return counts;
  }, [formData.items]);

  // Determine extra family based on items
  const extraFamily = useMemo(() => {
    // STEP 4F.2 · SINGLE source-of-truth = formData.extra_type (dropdown autoritativo).
    // Banner, categorie e note derivano tutti da qui, sincronizzati al cambio Tipo.
    if (formData.extra_type === 'lavoro') return 'lavoro';
    if (formData.extra_type === 'fornitura') return 'fornitura';
    // Fallback legacy: inferenza dalle categorie presenti (solo se extra_type mancante)
    const hasLavoroItems = formData.items.some(item => LAVORO_CATEGORIES.includes(item.category));
    const hasFornituraItems = formData.items.some(item => FORNITURA_CATEGORIES.includes(item.category));
    
    if (hasLavoroItems && !hasFornituraItems) return 'lavoro';
    if (hasFornituraItems && !hasLavoroItems) return 'fornitura';
    if (hasLavoroItems && hasFornituraItems) return 'mixed'; // Should not happen with validation
    return null; // No items yet, all categories available
  }, [formData.extra_type, formData.items]);

  // Determine which categories are disabled based on family
  const disabledCategories = useMemo(() => {
    if (!extraFamily) return []; // All enabled
    if (extraFamily === 'lavoro') return FORNITURA_CATEGORIES;
    if (extraFamily === 'fornitura') return LAVORO_CATEGORIES;
    return [];
  }, [extraFamily]);

  // Get family info message
  const familyInfoMessage = useMemo(() => {
    if (extraFamily === 'lavoro') {
      return {
        type: 'Lavori',
        payment: 'Integrato nei SAL residui del contratto principale',
        note: 'Per inserire forniture, finiture, serramenti o prodotti crea un nuovo extra dedicato.'
      };
    }
    if (extraFamily === 'fornitura') {
      return {
        type: 'Forniture',
        payment: '50% alla firma / 50% ad avviso merce pronta',
        note: 'Per inserire lavorazioni edili crea un nuovo extra dedicato.'
      };
    }
    return null;
  }, [extraFamily]);

  // Category totals for summary section
  const categoryTotals = useMemo(() => {
    const totals = {};
    CATEGORIES.forEach(cat => {
      totals[cat.code] = formData.items
        .filter(i => i.category === cat.code)
        .reduce((sum, i) => sum + (i.total || 0), 0);
    });
    return totals;
  }, [formData.items]);

  // Categories with items for summary display
  const categoriesWithItems = useMemo(
    () => CATEGORIES.filter(cat => categoryItemCounts[cat.code] > 0),
    [categoryItemCounts]
  );

  // Handle category change with unsaved check
  const handleCategoryChange = (newCategory) => {
    if (newCategory === activeCategory) return;
    
    if (hasUnsavedChanges) {
      setPendingCategory(newCategory);
      setShowChangeCategoryDialog(true);
    } else {
      setActiveCategory(newCategory);
      setSelectedSubcategory('all');
    }
  };

  const confirmCategoryChange = () => {
    if (pendingCategory) {
      setActiveCategory(pendingCategory);
      setSelectedSubcategory('all');
    }
    setShowChangeCategoryDialog(false);
    setPendingCategory(null);
  };

  const saveThenChangeCategory = async () => {
    await handleSaveForGuard();
    if (pendingCategory) {
      setActiveCategory(pendingCategory);
      setSelectedSubcategory('all');
    }
    setShowChangeCategoryDialog(false);
    setPendingCategory(null);
  };

  // Add item from price list
  const handleAddFromPriceList = (priceItem) => {
    const exists = formData.items.some(item => item.price_item_id === priceItem.id);
    if (exists) {
      toast.warning('Voce già presente');
      return;
    }
    
    const isLavoro = formData.extra_type === 'lavoro';
    // STEP 5B-1 · per LAVORI: listino = prezzo catalogo, netto proposto = listino * coeff categoria.
    const listPrice = priceItem.unit_price;
    const netPrice = isLavoro ? resolveProposedNet(activeCategory, listPrice) : priceItem.unit_price;
    const total = newItem.quantity * netPrice;

    const newLineItem = {
      id: `item-${Date.now()}`,
      price_item_id: priceItem.id,
      code: priceItem.code,
      description: priceItem.description,
      category: activeCategory,
      subcategory: priceItem.subcategory || '',
      quantity: newItem.quantity,
      unit: priceItem.unit,
      list_unit_price: isLavoro ? listPrice : null,
      unit_price: netPrice,
      total: total,
      vat_percentage: priceItem.vat_percentage || 10,
      notes: '',
      quantity_mode: 'single',
      measurement_lines: []
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newLineItem]
    }));
    
    setNewItem(prev => ({ ...prev, quantity: 1 }));
    toast.success('Voce aggiunta');
  };

  // Add manual item
  const handleAddManualItem = () => {
    if (!newItem.code || !newItem.description) {
      toast.error('Inserisci codice e descrizione');
      return;
    }
    
    const total = newItem.quantity * newItem.unit_price;
    
    const newLineItem = {
      id: `item-${Date.now()}`,
      price_item_id: null,
      code: newItem.code,
      description: newItem.description,
      category: activeCategory,
      subcategory: newItem.subcategory || '',
      quantity: newItem.quantity,
      unit: newItem.unit || 'nr',
      unit_price: newItem.unit_price,
      total: total,
      vat_percentage: newItem.vat_percentage || 10,
      notes: newItem.notes || '',
      quantity_mode: 'single',
      measurement_lines: []
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newLineItem]
    }));
    
    setNewItem({
      price_item_id: '',
      code: '',
      description: '',
      category: '',
      subcategory: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      vat_percentage: 10,
      notes: ''
    });
    
    setShowAddItemModal(false);
    toast.success('Voce aggiunta');
  };

  // Update item
  const handleUpdateItem = (itemId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unit_price') {
            updated.total = (updated.quantity || 0) * (updated.unit_price || 0);
          }
          return updated;
        }
        return item;
      })
    }));
  };

  // Remove item
  const handleRemoveItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    toast.success('Voce rimossa');
  };

  // Save extra
  const handleSave = async () => {
    if (!formData.title || formData.title.trim() === '') {
      toast.error('Inserisci il titolo dell\'extra');
      return;
    }
    
    if (!formData.project_id) {
      toast.error('Progetto non valido');
      return;
    }
    
    if (formData.items.length === 0) {
      toast.error('Aggiungi almeno una voce all\'extra');
      return;
    }
    
    setSaving(true);
    try {
      // STEP 4F.2/E · sanitize: una Fornitura non deve MAI trasportare metadata misure
      // (le misure sono solo per Extra lavoro). Evita contaminazione dopo switch lavoro→fornitura.
      const sanitizedItems = formData.extra_type === 'fornitura'
        ? formData.items.map((it) => ({ ...it, quantity_mode: 'single', measurement_lines: [] }))
        : formData.items;
      const payload = {
        ...formData,
        items: sanitizedItems,
        total: grandTotal
      };
      
      if (isNew) {
        await extraApi.create(payload);
        toast.success('Extra creato con successo');
        setHasUnsavedChanges(false);
        initialFormDataRef.current = JSON.stringify(formData);
        navigate(`/projects/${formData.project_id}`);
      } else {
        await extraApi.update(id, payload);
        toast.success('Extra salvato');
        setHasUnsavedChanges(false);
        initialFormDataRef.current = JSON.stringify(formData);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!id || isNew) {
      toast.error('Salva prima l\'extra per generare il PDF');
      return;
    }
    
    setDownloading(true);
    try {
      const response = await extraApi.generatePdf(id);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${extra?.extra_number || 'extra'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.response?.data?.detail || 'Errore nella generazione del PDF');
    } finally {
      setDownloading(false);
    }
  };

  // ========== WORKFLOW HANDLERS ==========
  
  const handleSendToClient = async () => {
    if (!id) return;
    setProcessingAction(true);
    try {
      await extraApi.sendToClient(id);
      setExtra(prev => ({ ...prev, approval_status: 'in_attesa_firma' }));
      toast.success('Extra inviato al cliente per la firma');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio al cliente');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleUploadSigned = async () => {
    if (!id || !selectedSignedFile) return;
    
    setUploadingFile(true);
    try {
      const response = await extraApi.uploadSigned(id, selectedSignedFile);
      setExtra(prev => ({ 
        ...prev, 
        approval_status: response.data.status, 
        signed_pdf_url: extraApi.getSignedPdfUrl(id),
        signed_pdf_filename: response.data.filename
      }));
      toast.success('PDF firmato caricato con successo!');
      setShowUploadSignedDialog(false);
      setSelectedSignedFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Seleziona un file PDF');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Il file non può superare i 10MB');
        return;
      }
      setSelectedSignedFile(file);
    }
  };

  const handleUploadSupplierDocs = async () => {
    if (!id || !selectedSupplierFile) return;
    
    setUploadingFile(true);
    try {
      const response = await extraApi.uploadSupplierDocs(id, selectedSupplierFile, supplierDocType);
      setExtra(prev => ({ 
        ...prev, 
        approval_status: response.data.status,
        supplier_quote_url: response.data.supplier_quote_url || prev.supplier_quote_url,
        supplier_order_url: response.data.supplier_order_url || prev.supplier_order_url
      }));
      toast.success('Documento fornitore caricato con successo!');
      setShowUploadSupplierDialog(false);
      setSelectedSupplierFile(null);
      setSupplierDocType('preventivo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSupplierFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Seleziona un file PDF');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Il file non può superare i 10MB');
        return;
      }
      setSelectedSupplierFile(file);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setProcessingAction(true);
    try {
      await extraApi.approve(id);
      setExtra(prev => ({ ...prev, approval_status: 'approvato' }));
      toast.success('Extra approvato! Le voci saranno aggiunte al SAL.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'approvazione');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectReason.trim()) {
      toast.error('Inserisci un motivo per il rifiuto');
      return;
    }
    setProcessingAction(true);
    try {
      await extraApi.reject(id, rejectReason);
      setExtra(prev => ({ ...prev, approval_status: 'rifiutato' }));
      setShowRejectDialog(false);
      setRejectReason('');
      toast.success('Extra rifiutato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rifiuto');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    setProcessingAction(true);
    try {
      await extraApi.complete(id);
      setExtra(prev => ({ ...prev, approval_status: 'completato' }));
      toast.success('Extra completato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel completamento');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRequestModification = async () => {
    if (!id || !modificationNotes.trim()) {
      toast.error('Inserisci le note di modifica');
      return;
    }
    setProcessingAction(true);
    try {
      await extraApi.requestModification(id, modificationNotes);
      setExtra(prev => ({ 
        ...prev, 
        approval_status: 'modifica_richiesta',
        modification_requested: true,
        modification_notes: modificationNotes 
      }));
      setShowModificationDialog(false);
      setModificationNotes('');
      toast.success('Richiesta di modifica inviata');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleValidate = async () => {
    if (!id) return;
    setProcessingAction(true);
    try {
      const response = await extraApi.validate(id);
      setExtra(prev => ({ 
        ...prev, 
        approval_status: response.data.new_status 
      }));
      toast.success('Extra validato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella validazione');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!id) return;
    if (discountPercentage < 0 || discountPercentage > 100) {
      toast.error('Lo sconto deve essere tra 0 e 100%');
      return;
    }
    setProcessingAction(true);
    try {
      const response = await extraApi.applyDiscount(id, discountPercentage);
      setExtra(response.data);
      setShowDiscountDialog(false);
      setDiscountPercentage(0);
      toast.success(`Sconto del ${discountPercentage}% applicato`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'applicazione dello sconto');
    } finally {
      setProcessingAction(false);
    }
  };

  // Current category object
  const currentCategory = CATEGORIES.find(c => c.code === activeCategory);

  // Extra contract-effective (firmato/accettato) → il Tipo non è editabile dall'editor
  // normale; la correzione passa dall'azione Admin "Correggi classificazione".
  const CONTRACT_EFFECTIVE_STATUSES = ['firmato_cliente','in_attesa_approvazione','in_validazione','validato','in_attesa_ordine','ordinato','completato','accettato_cliente','firmato','signed','final','definitivo'];
  const isContractEffective = !!extra && (
    CONTRACT_EFFECTIVE_STATUSES.includes((extra.approval_status || extra.status || '').toLowerCase())
    || !!extra.signed_at || !!extra.signed_pdf_url
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="extra-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => safeNavigate(`/projects/${formData.project_id}`)}
            className="text-[#737373]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Indietro
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-h3">
                {isNew ? 'Nuovo Extra' : extra?.extra_number || 'Extra'}
              </h1>
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                  Non salvato
                </Badge>
              )}
            </div>
            <p className="text-body-small mt-1">
              Progetto: {project?.project_number || '-'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canStorno && (
            <Button variant="outline" onClick={() => setStornoOpen(true)}
              className="border-red-200 text-red-700 hover:bg-red-50" data-testid="storna-extra-btn">
              <FileMinus className="w-4 h-4 mr-2" /> Storna / rettifica Extra
            </Button>
          )}
          {!isNew && (
            <Button 
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloading || formData.items.length === 0}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? 'Generazione...' : 'Scarica PDF'}
            </Button>
          )}
          <Button 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || formData.items.length === 0}
            data-testid="save-extra-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Extra'}
          </Button>
        </div>
      </div>

      {/* Riepilogo Rettifiche/Storni (read-only) — valore corrente vs originario */}
      {!isNew && extra?.corrections_summary && (() => {
        const cs = extra.corrections_summary;
        const state = cs.economic_correction_state;
        const hasCorr = state === 'stornato' || state === 'parzialmente_stornato';
        const badge = state === 'stornato'
          ? { label: 'Stornato', cls: 'bg-red-100 text-red-700' }
          : (state === 'parzialmente_stornato' ? { label: 'Parzialmente stornato', cls: 'bg-orange-100 text-orange-700' } : null);
        const workflow = cs.has_fiscal_hold_correction
          ? { label: 'Rettifica firmata · da regolarizzare', cls: 'bg-amber-100 text-amber-800' }
          : ((cs.has_pending_correction || cs.has_signed_correction) ? { label: 'Rettifica in corso', cls: 'bg-slate-100 text-slate-600' } : null);
        if (!hasCorr && !workflow) return null;
        const fmt = (v) => (v ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
        return (
          <div className="rounded-lg border border-[#ECECEC] bg-[#FAFAFA] p-4" data-testid="extra-detail-corrections-summary">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="text-sm font-semibold text-[#171717]">Valore Extra</span>
              {badge && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`} data-testid="extra-detail-correction-badge">{badge.label}</span>}
              {workflow && <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${workflow.cls}`} data-testid="extra-detail-correction-workflow">{workflow.label}</span>}
            </div>
            {hasCorr ? (
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="text-xs text-[#737373]">Originario</div><div className="font-semibold tabular-nums" data-testid="extra-detail-original-net">{fmt(cs.original_net)} €</div></div>
                <div><div className="text-xs text-[#737373]">Rettifiche</div><div className="font-semibold text-red-600 tabular-nums" data-testid="extra-detail-corrections-net">{fmt(cs.corrections_net_effective)} €</div></div>
                <div><div className="text-xs text-[#737373]">Corrente</div><div className="font-bold text-[#3C3C3C] tabular-nums" data-testid="extra-detail-current-net">{fmt(cs.current_net)} €</div></div>
              </div>
            ) : (
              <div className="text-xs text-[#737373]">Le rettifiche non ancora effettive non modificano il valore corrente ({fmt(cs.current_net)} €).</div>
            )}
          </div>
        );
      })()}

      {/* Timeline Rettifiche / Storni */}
      {!isNew && corrections.length > 0 && (
        <div className="rounded-lg border border-[#ECECEC] p-4" data-testid="corrections-timeline">
          <div className="text-sm font-semibold text-[#171717] mb-3">Rettifiche / Storni</div>
          <div className="space-y-2">
            {corrections.map((c) => (
              <button type="button" key={c.id} onClick={() => setCorrectionDetailId(c.id)}
                className="w-full text-left rounded-lg border border-[#F0F0F0] hover:bg-[#FAFAFA] p-3 transition-colors"
                data-testid={`correction-row-${c.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#171717]">{c.correction_number}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${CORRECTION_STATUS_TONE[c.status] || 'bg-slate-100 text-slate-600'}`} data-testid={`correction-status-${c.id}`}>{CORRECTION_STATUS_LABELS[c.status] || c.status}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{c.correction_type === 'full' ? 'Totale' : 'Parziale'}</span>
                  </div>
                  <span className="text-sm font-semibold text-red-600 tabular-nums">{(c.net_delta ?? 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                </div>
                <div className="text-xs text-[#8a8a8a] mt-1">{(c.created_at || '').slice(0, 10)} · {c.reason}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {extra && (
        <ExtraStornoDialog open={stornoOpen} onOpenChange={setStornoOpen} extra={extra} onCreated={reloadAfterCorrection} />
      )}
      <CorrectionDetailDialog open={!!correctionDetailId} onOpenChange={(v) => !v && setCorrectionDetailId(null)}
        correctionId={correctionDetailId} originalExtra={extra} isAdmin={isAdmin} onUpdated={reloadAfterCorrection} />


      {/* Workflow Status Card - Solo per extra esistenti */}
      {!isNew && extra && (
        <ExtraStatusCard
          extra={extra}
          isAdmin={isAdmin}
          isTechnician={isTechnician}
          isAdminOrAdministration={isAdminOrAdministration}
          processingAction={processingAction}
          downloading={downloading}
          formData={formData}
          onSendToClient={handleSendToClient}
          onApprove={handleApprove}
          onComplete={handleComplete}
          onValidate={handleValidate}
          onDownloadPdf={handleDownloadPdf}
          setShowRejectDialog={setShowRejectDialog}
          setShowUploadSignedDialog={setShowUploadSignedDialog}
          setShowUploadSupplierDialog={setShowUploadSupplierDialog}
          setShowModificationDialog={setShowModificationDialog}
          setShowDiscountDialog={setShowDiscountDialog}
        />
      )}

      {/* Extra Info Card */}
      <ExtraInfoCard
        formData={formData}
        setFormData={setFormData}
        grandTotal={grandTotal}
        isAdminOrAdministration={isAdminOrAdministration}
        isContractEffective={isContractEffective}
        onReclassify={() => setShowReclassifyDialog(true)}
      />

      {/* Category Tabs */}
      <ExtraCategoryTabs
        categories={FILTERED_CATEGORIES}
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        categoryItemCounts={categoryItemCounts}
        disabledCategories={disabledCategories}
      />

      {/* Family Info Banner */}
      {familyInfoMessage && (
        <div className="bg-[#FFF0E5] border border-[#FF7819]/30 rounded-lg p-4 mb-4" data-testid="extra-family-info">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF7819]/10 flex items-center justify-center">
              <svg className="w-4 h-4 text-[#FF7819]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-[#3C3C3C] mb-1">Tipo extra: {familyInfoMessage.type}</div>
              <div className="text-sm text-[#737373] mb-1">
                <span className="font-medium">Pagamento:</span> {familyInfoMessage.payment}
              </div>
              <div className="text-xs text-[#737373] italic">{familyInfoMessage.note}</div>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <ExtraItemsTable
        currentCategory={currentCategory}
        categoryItems={categoryItems}
        categoryTotal={categoryTotal}
        formData={formData}
        setFormData={setFormData}
        activeCategory={activeCategory}
        onShowAddItemModal={() => setShowAddItemModal(true)}
        onUpdateItem={handleUpdateItem}
        onRemoveItem={handleRemoveItem}
        editingNoteId={editingNoteId}
        setEditingNoteId={setEditingNoteId}
        editingNoteValue={editingNoteValue}
        setEditingNoteValue={setEditingNoteValue}
        extraType={formData.extra_type}
      />

      {/* Summary Card */}
      <ExtraSummaryCard
        categoriesWithItems={categoriesWithItems}
        categoryTotals={categoryTotals}
        grandTotal={grandTotal}
        formData={formData}
        setFormData={setFormData}
      />

      {/* Add Item Modal */}
      <ExtraAddItemModal
        open={showAddItemModal}
        onOpenChange={setShowAddItemModal}
        currentCategory={currentCategory}
        subcategories={subcategories}
        selectedSubcategory={selectedSubcategory}
        setSelectedSubcategory={setSelectedSubcategory}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        newItem={newItem}
        setNewItem={setNewItem}
        filteredPriceList={filteredPriceList}
        onAddFromPriceList={handleAddFromPriceList}
        onAddManualItem={handleAddManualItem}
      />

      {/* Dialogs */}
      <ChangeCategoryDialog
        open={showChangeCategoryDialog}
        onOpenChange={setShowChangeCategoryDialog}
        onSaveThenChange={saveThenChangeCategory}
        onConfirmChange={confirmCategoryChange}
      />

      <RejectDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onReject={handleReject}
        processingAction={processingAction}
      />

      <UploadSignedDialog
        open={showUploadSignedDialog}
        onOpenChange={(open) => {
          setShowUploadSignedDialog(open);
          if (!open) setSelectedSignedFile(null);
        }}
        selectedFile={selectedSignedFile}
        onFileSelect={handleFileSelect}
        onUpload={handleUploadSigned}
        uploadingFile={uploadingFile}
      />

      <UploadSupplierDialog
        open={showUploadSupplierDialog}
        onOpenChange={(open) => {
          setShowUploadSupplierDialog(open);
          if (!open) {
            setSelectedSupplierFile(null);
            setSupplierDocType('preventivo');
          }
        }}
        selectedFile={selectedSupplierFile}
        onFileSelect={handleSupplierFileSelect}
        supplierDocType={supplierDocType}
        setSupplierDocType={setSupplierDocType}
        onUpload={handleUploadSupplierDocs}
        uploadingFile={uploadingFile}
      />

      <ModificationDialog
        open={showModificationDialog}
        onOpenChange={setShowModificationDialog}
        modificationNotes={modificationNotes}
        setModificationNotes={setModificationNotes}
        onRequestModification={handleRequestModification}
        processingAction={processingAction}
      />

      <DiscountDialog
        open={showDiscountDialog}
        onOpenChange={setShowDiscountDialog}
        discountPercentage={discountPercentage}
        setDiscountPercentage={setDiscountPercentage}
        onApplyDiscount={handleApplyDiscount}
        processingAction={processingAction}
      />

      <ReclassifyDialog
        open={showReclassifyDialog}
        onOpenChange={setShowReclassifyDialog}
        extraId={id}
        onSuccess={() => window.location.reload()}
      />
    </div>
  );
};

export default ExtraDetailPage;
